import * as React from "react";
import * as LocalForage from "localforage";

import { GenericDataSource_CapableOfBeingIntersected } from "../../../Common";

import { CheckoutModel_MyBag, MyBagTotals_Response } from "../../model";

export interface DataMutableSourceSuccessClientStorageDataConsumerInterface {
  isLoading: boolean;
}
const defaultValue =
  {} as unknown as DataMutableSourceSuccessClientStorageDataConsumerInterface;
const DataMutableSourceSuccessClientStorageDataContext =
  React.createContext(defaultValue);

export interface DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectableConsumer
  extends GenericDataSource_CapableOfBeingIntersected<CheckoutModel_MyBag> {}
const defaultValue_myBagGenericIntersectable =
  {} as unknown as DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectableConsumer;
const DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable_Context =
  React.createContext(defaultValue_myBagGenericIntersectable);

export interface DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectableConsumer
  extends GenericDataSource_CapableOfBeingIntersected<MyBagTotals_Response> {}
const defaultValue_myBagTotalsGenericIntersectable =
  {} as unknown as DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectableConsumer;
const DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectable_Context =
  React.createContext(defaultValue_myBagTotalsGenericIntersectable);

export interface DataMutableSourceSuccessClientStorageData_ControllerInterface {
  handleRequireCacheData: () => void;
  handleLatestMyBagDataChange: (
    latestMyBagData: CheckoutModel_MyBag | undefined
  ) => Promise<void>;
  handleLatestMyBagTotalsDataChange: (
    latestMyBagTotalsData: MyBagTotals_Response | undefined
  ) => Promise<void>;
}
const controllerDefaultValue =
  {} as unknown as DataMutableSourceSuccessClientStorageData_ControllerInterface;
const DataMutableSourceSuccessClientStorageData_ControllerContext =
  React.createContext(controllerDefaultValue);

enum DataMutableSourceSuccessClientStorageDataKey {
  LatestMyBagData = "LatestMyBagData",
  LatestMyBagTotalsData = "LatestMyBagTotalsData",
}

export const DataMutableSourceSuccessClientStorageDataProvider: React.FunctionComponent =
  (props) => {
    const [requireCacheData, setRequireCacheData] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const [cached_myBag, setCached_myBag] = React.useState();
    const [dataUpdatedAt_myBag, setDataUpdatedAt_myBag] = React.useState(-1);
    const [errorUpdatedAt_myBag, setErrorUpdatedAt_myBag] = React.useState(-1);
    const [error_myBag, setError_myBag] = React.useState<
      unknown | null | undefined
    >();

    const [cached_myBagTotals, setCached_MyBagTotals] = React.useState();
    const [dataUpdatedAt_myBagTotals, setDataUpdatedAt_myBagTotals] =
      React.useState(-1);
    const [errorUpdatedAt_myBagTotals, setErrorUpdatedAt_myBagTotals] =
      React.useState(-1);
    const [error_myBagTotals, setError_myBagTotals] = React.useState<
      unknown | null | undefined
    >();

    const status_myBag: GenericDataSource_CapableOfBeingIntersected<any>["status"] =
      React.useMemo(() => {
        if (isLoading) {
          return "loading";
        }
        if (error_myBag && cached_myBag) {
          if (errorUpdatedAt_myBag > dataUpdatedAt_myBag) {
            return "error";
          } else {
            return "success";
          }
        }
        if (error_myBag) {
          return "error";
        }
        if (cached_myBag) {
          return "success";
        }
        return "idle";
      }, [
        isLoading,
        error_myBag,
        cached_myBag,
        errorUpdatedAt_myBag,
        dataUpdatedAt_myBag,
      ]);

    const status_myBagTotals: GenericDataSource_CapableOfBeingIntersected<any>["status"] =
      React.useMemo(() => {
        if (isLoading) {
          return "loading";
        }
        if (error_myBagTotals && cached_myBagTotals) {
          if (errorUpdatedAt_myBagTotals > dataUpdatedAt_myBagTotals) {
            return "error";
          } else {
            return "success";
          }
        }
        if (error_myBagTotals) {
          return "error";
        }
        if (cached_myBagTotals) {
          return "success";
        }
        return "idle";
      }, [
        isLoading,
        error_myBagTotals,
        cached_myBagTotals,
        errorUpdatedAt_myBagTotals,
        dataUpdatedAt_myBagTotals,
      ]);

    const initialize = React.useCallback(async () => {
      if (!requireCacheData) {
        return;
      }
      setIsLoading(true);

      const cachedMyBagData_storedItem = await LocalForage.getItem<string>(
        DataMutableSourceSuccessClientStorageDataKey.LatestMyBagData
      );
      if (cachedMyBagData_storedItem) {
        try {
          const cachedMyBagData_parsedJson = JSON.parse(
            cachedMyBagData_storedItem
          );
          await LocalForage.removeItem(
            DataMutableSourceSuccessClientStorageDataKey.LatestMyBagData
          );

          setCached_myBag(cachedMyBagData_parsedJson);
          setDataUpdatedAt_myBag(Date.now());
        } catch (anyError) {
          setError_myBag(anyError);
          setErrorUpdatedAt_myBag(Date.now());
        }
      }

      const cachedMyBagTotalsData_storedItem =
        await LocalForage.getItem<string>(
          DataMutableSourceSuccessClientStorageDataKey.LatestMyBagTotalsData
        );
      if (cachedMyBagTotalsData_storedItem) {
        try {
          const cachedMyBagTotalsData_parsedJson = JSON.parse(
            cachedMyBagTotalsData_storedItem
          );
          await LocalForage.removeItem(
            DataMutableSourceSuccessClientStorageDataKey.LatestMyBagTotalsData
          );

          setCached_MyBagTotals(cachedMyBagTotalsData_parsedJson);
          setDataUpdatedAt_myBagTotals(Date.now());
        } catch (anyError) {
          setError_myBagTotals(anyError);
          setErrorUpdatedAt_myBagTotals(Date.now());
        }
      }

      setIsLoading(false);
    }, [
      requireCacheData,
      setCached_myBag,
      setDataUpdatedAt_myBag,
      setError_myBag,
      setErrorUpdatedAt_myBag,
      setIsLoading,
    ]);
    React.useEffect(() => {
      initialize();
    }, [requireCacheData]);

    const handleRequireCacheData = React.useCallback(async () => {
      setRequireCacheData(true);
    }, [setRequireCacheData]);

    const handleLatestMyBagDataChange: DataMutableSourceSuccessClientStorageData_ControllerInterface["handleLatestMyBagDataChange"] =
      React.useCallback(
        async (latestMyBagData) => {
          if (cached_myBag === latestMyBagData) {
            return;
          }

          let latestMyBagData_stringifiedJson: string | undefined;
          try {
            latestMyBagData_stringifiedJson = JSON.stringify(latestMyBagData);
          } catch (anyError) {}
          await LocalForage.setItem(
            DataMutableSourceSuccessClientStorageDataKey.LatestMyBagData,
            latestMyBagData_stringifiedJson
          );
        },
        [cached_myBag]
      );
    const handleLatestMyBagTotalsDataChange: DataMutableSourceSuccessClientStorageData_ControllerInterface["handleLatestMyBagTotalsDataChange"] =
      React.useCallback(
        async (latestMyBagTotalsData) => {
          if (cached_myBagTotals === latestMyBagTotalsData) {
            return;
          }
          let latestMyBagTotalsData_stringifiedJson: string | undefined;
          try {
            latestMyBagTotalsData_stringifiedJson = JSON.stringify(
              latestMyBagTotalsData
            );
          } catch (anyError) {}
          await LocalForage.setItem(
            DataMutableSourceSuccessClientStorageDataKey.LatestMyBagTotalsData,
            latestMyBagTotalsData_stringifiedJson
          );
        },
        [cached_myBagTotals]
      );

    const value: DataMutableSourceSuccessClientStorageDataConsumerInterface =
      React.useMemo(
        () => ({
          isLoading: !!isLoading,
        }),
        [isLoading]
      );
    const myBagGenericIntersectableValue: DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectableConsumer =
      React.useMemo(
        () => ({
          dataUpdatedAt: dataUpdatedAt_myBag,
          data: cached_myBag,
          errorUpdatedAt: errorUpdatedAt_myBag,
          error: error_myBag,
          status: status_myBag,
        }),
        [
          dataUpdatedAt_myBag,
          cached_myBag,
          errorUpdatedAt_myBag,
          error_myBag,
          status_myBag,
        ]
      );

    const myBagTotalsGenericIntersectableValue: DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectableConsumer =
      React.useMemo(
        () => ({
          dataUpdatedAt: dataUpdatedAt_myBagTotals,
          data: cached_myBagTotals,
          errorUpdatedAt: errorUpdatedAt_myBagTotals,
          error: error_myBagTotals,
          status: status_myBagTotals,
        }),
        [
          dataUpdatedAt_myBagTotals,
          cached_myBagTotals,
          errorUpdatedAt_myBagTotals,
          error_myBagTotals,
          status_myBagTotals,
        ]
      );
    const controllerValue: DataMutableSourceSuccessClientStorageData_ControllerInterface =
      React.useMemo(
        () => ({
          handleRequireCacheData,
          handleLatestMyBagDataChange,
          handleLatestMyBagTotalsDataChange,
        }),
        [
          handleRequireCacheData,
          handleLatestMyBagDataChange,
          handleLatestMyBagTotalsDataChange,
        ]
      );
    return (
      <DataMutableSourceSuccessClientStorageDataContext.Provider value={value}>
        <DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable_Context.Provider
          value={myBagGenericIntersectableValue}
        >
          <DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectable_Context.Provider
            value={myBagTotalsGenericIntersectableValue}
          >
            <DataMutableSourceSuccessClientStorageData_ControllerContext.Provider
              value={controllerValue}
            >
              {props?.children || []}
            </DataMutableSourceSuccessClientStorageData_ControllerContext.Provider>
          </DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectable_Context.Provider>
        </DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable_Context.Provider>
      </DataMutableSourceSuccessClientStorageDataContext.Provider>
    );
  };

export const useDataMutableSourceSuccessClientStorageData = () => {
  const context = React.useContext(
    DataMutableSourceSuccessClientStorageDataContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutableSourceSuccessClientStorageData must be used within a DataMutableSourceSuccessClientStorageDataProvider"
    );
  }
  return context;
};

export const useDataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable =
  () => {
    const context = React.useContext(
      DataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable_Context
    );
    if (context === defaultValue_myBagGenericIntersectable) {
      throw new Error(
        "useDataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable must be used within a DataMutableSourceSuccessClientStorageDataProvider"
      );
    }
    return context;
  };

export const useDataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectable =
  () => {
    const context = React.useContext(
      DataMutableSourceSuccessClientStorageData_MyBagTotals_GenericIntersectable_Context
    );
    if (context === defaultValue_myBagTotalsGenericIntersectable) {
      throw new Error(
        "useDataMutableSourceSuccessClientStorageData_MyBag_GenericIntersectable must be used within a DataMutableSourceSuccessClientStorageDataProvider"
      );
    }
    return context;
  };

export const useDataMutableSourceSuccessClientStorageData_asController = () => {
  const context = React.useContext(
    DataMutableSourceSuccessClientStorageData_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutableSourceSuccessClientStorageData_asController must be used within a DataMutableSourceSuccessClientStorageDataProvider"
    );
  }
  return context;
};
